<script setup>
/**
 * One card in a `SettingsCardGrid`: icon and name, a status chip, a line of
 * description, and an action at the bottom.
 *
 * Text.com's rule for the action is worth keeping: a thing you have not set up
 * yet gets a solid button (`Connect`), a thing you already have gets a quiet
 * text link (`Manage`). The card tells you what to do next without a legend.
 */
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsStatusChip from './SettingsStatusChip.vue';

defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  icon: { type: String, default: '' },
  statusLabel: { type: String, default: '' },
  statusTone: { type: String, default: 'neutral' },
  actionLabel: { type: String, default: '' },
  // A configured card offers a quiet `Manage` link; an unconfigured one gets
  // the solid button that draws the eye.
  isConfigured: { type: Boolean, default: false },
});

const emit = defineEmits(['action']);
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container min-w-0"
  >
    <div class="flex items-start justify-between gap-3 min-w-0">
      <div class="flex items-center gap-2 min-w-0">
        <span v-if="icon" :class="icon" class="size-5 text-n-slate-11" />
        <h3 class="text-sm font-medium truncate text-n-slate-12">
          {{ title }}
        </h3>
      </div>
      <SettingsStatusChip
        v-if="statusLabel"
        :label="statusLabel"
        :tone="statusTone"
      />
    </div>

    <p v-if="description" class="flex-1 text-sm text-n-slate-11">
      {{ description }}
    </p>

    <div v-if="actionLabel" class="flex">
      <button
        v-if="isConfigured"
        type="button"
        class="text-sm underline text-n-slate-12 underline-offset-4 hover:text-n-slate-11"
        @click="emit('action')"
      >
        {{ actionLabel }}
      </button>
      <Button v-else size="sm" :label="actionLabel" @click="emit('action')" />
    </div>
  </div>
</template>
